"use client";

import { useEffect } from "react";

/** Below this width a wide table is always cards — a table is unreadable there. */
const CARD_BREAKPOINT = 768;
/**
 * Per-column text-length thresholds that earn a minimum width on wide screens.
 * Auto table layout sizes columns to fit the container, which squeezes the
 * essay column narrow and makes the row tall — on the FDA table a
 * 972-character cell got 208px and stretched its row to 1,222px. A floor on
 * that one column drops the row to ~480px; spreading the same width over every
 * column costs three times the horizontal scroll and buys nothing.
 *
 * Measured against the column's MEDIAN cell, never its longest. A column's
 * width is shared by all its cells, so sizing to an outlier would pad out a
 * column of otherwise-short values (a "Definition" column of 60-80 character
 * cells with one 166-character entry gets no floor, and shouldn't).
 */
const COL_LONG_CHARS = 400;
const COL_MEDIUM_CHARS = 120;

/**
 * In card mode the fields sit two abreast, which is what stops a scalar-heavy
 * record (an amount, a date, a market cap) from running a screen and a half of
 * label-over-value rows. A column whose median cell is longer than this reads
 * as a sentence rather than a value, so it spans the card's full width
 * instead. Far below COL_MEDIUM_CHARS on purpose: a half-width field is ~200px,
 * so text turns awkward long before it earns a desktop min-width. Tuned down
 * from 60 — at 60 a category like "Material weakness and change of independent
 * registered public accounting firm" stayed in the scalar grid and got set as a
 * four-line bold value, which read like the record's headline.
 */
const CARD_SPAN_CHARS = 40;

/**
 * A figure, so card mode can set it as one. These tables are mostly text with a
 * few numbers in them, and the numbers are the reason the record exists — an
 * amount, a delta, a market cap, a percentage — so they carry the card's type
 * hierarchy. Deliberately strict: a date ("2026-03-10"), a bare year, or
 * anything with prose around it stays a plain value. `\p{Sc}` is any currency
 * symbol, so ¥ and € work without listing them.
 */
const NUMERIC_VALUE = /^\p{Sc}?\s?-?\d[\d,.]*\s?(billion|million|trillion|thousand|bn|mn|[bmkt%×x])?$/iu;
const BARE_YEAR = /^\d{4}$/;

/**
 * Cells longer than this are eligible for the collapse toggle. It is a coarse
 * gate on purpose — a character count is stable across layout modes, where a
 * height measurement would flip when the table re-lays out — and the toggle
 * itself is then shown only where the text measurably overflows. Deliberately
 * low: in a squeezed 140px column even 150 characters runs a dozen lines.
 */
const LONG_CELL_CHARS = 140;

/**
 * Client-side progressive enhancement for article tables.
 *
 * Renders nothing. On mount it finds every `.prose-custom table`, wraps it in a
 * positioned `.table-scroll` element, and then does three things:
 *
 * **1. Scroll affordance.** Toggles `data-scrollable` / `data-at-start` /
 * `data-at-end` on the wrapper from the *measured* overflow and scroll
 * position. The edge fades (see globals.css) key off those attributes, so a
 * table that fits shows no fade, a wide table shows a right fade until you
 * reach the end, and the left fade appears only once you've scrolled away from
 * the start. A table that actually overflows also gets `tabindex="0"` so the
 * scroll region is keyboard-operable (WCAG 2.1.1). We deliberately do NOT add
 * `role="region"`: that would override the table's implicit role and break
 * screen-reader row/column navigation.
 *
 * **2. Long-cell collapse, wherever cells are full-width blocks.** Dataset
 * tables carry
 * paragraph-length cells (the FDA/legal-action tables run ~1,000 characters in
 * one cell), which are unreadable in a 350px-wide column. Each long cell's
 * children move into a `.cell-clamp` div — the clamp has to live on an inner
 * element because `-webkit-line-clamp` needs `display: -webkit-box`, which
 * would destroy `display: table-cell` — and get a "Show more" toggle. The
 * toggle only appears when the text is genuinely cut off (measured, so a cell
 * that happens to fit shows no false affordance). It applies on every phone
 * and in the card view at any width — keyed off `data-collapse` on the wrapper
 * — but never in the desktop table view, where a cell is a column the reader
 * takes in whole and every cell renders in full.
 *
 * **2b. Column floors.** Each column is tagged `data-col="long" | "medium"`
 * from its *median* cell — a column's width is shared, so an outlier must not
 * pad out a column of short values — and CSS turns that into a minimum width
 * above the card breakpoint. Without it, auto table layout squeezes the one
 * essay column narrow to make everything fit and the row grows past 1,200px.
 *
 * **3. Card mode.** A nine-column table cannot be read 350px at a time. A
 * table that is both wide and properly headed gets `.as-cards` on its wrapper:
 * each row becomes a card and each cell a labelled field (`data-label`, copied
 * from the matching `<th>`), so there is no horizontal scrolling at all. Below
 * `CARD_BREAKPOINT` that is forced for tables too wide to read as tables;
 * otherwise each table starts in the view that suits it — cards if it would
 * need horizontal scrolling, the table itself if it already fits — and a
 * `.table-view-toggle` above every table that can take one lets the reader
 * override it. The override is scoped to that one table: a switch governs the
 * table it sits above, and nothing on another article changes because of it.
 * The choice is remembered across articles in `localStorage` and applies to
 * every table on the page at once. `data-wide` is measured once in table mode
 * and then sticky — in card mode nothing overflows, so re-measuring would flip
 * it straight back. Tables without a `<thead>`, or whose rows don't all match
 * the header's cell count (colspan/rowspan), are left as tables: index-mapped
 * labels would be wrong, and a wrong label is worse than a scroll.
 *
 * It queries the DOM rather than wrapping the prose as React children so it
 * never takes ownership of the server-rendered article tree (that subtree is
 * not re-rendered on the client, so moving its nodes here is safe).
 */
export default function TableScrollAffordance() {
  useEffect(() => {
    const syncs: Array<() => void> = [];
    const cleanups: Array<() => void> = [];

    document.querySelectorAll<HTMLTableElement>(".prose-custom table").forEach((table) => {
      // Wrap the table in a non-scrolling positioning context (idempotent).
      let wrap = table.parentElement;
      if (!wrap || !wrap.classList.contains("table-scroll")) {
        const created = document.createElement("div");
        created.className = "table-scroll";
        // Move the table's vertical margins onto the wrapper so spacing is
        // unchanged and the absolutely-positioned fades hug the table edges.
        const cs = getComputedStyle(table);
        created.style.marginTop = cs.marginTop;
        created.style.marginBottom = cs.marginBottom;
        table.parentNode?.insertBefore(created, table);
        created.appendChild(table);
        table.style.marginTop = "0";
        table.style.marginBottom = "0";
        wrap = created;
      }
      const wrapEl = wrap as HTMLDivElement;

      const headers = [...table.querySelectorAll<HTMLTableCellElement>("thead th")].map((th) =>
        (th.textContent || "").trim(),
      );
      const rows = [...table.querySelectorAll<HTMLTableRowElement>("tbody tr")];

      // Label cells for card mode — only when every row lines up with the
      // header, otherwise the labels would be attached to the wrong values.
      const labelled =
        headers.length > 1 && rows.length > 0 && rows.every((r) => r.cells.length === headers.length);
      if (labelled) {
        rows.forEach((row) => {
          [...row.cells].forEach((cell, i) => {
            cell.dataset.label = headers[i];
            if (!(cell.textContent || "").trim()) cell.dataset.empty = "true";
          });
        });
      }
      wrapEl.dataset.labelled = String(labelled);

      // Tag columns by their longest cell so CSS can give the text-heavy ones a
      // minimum width (desktop only — in card mode a min-width would force the
      // sideways scroll that card mode exists to remove).
      if (headers.length) {
        headers.forEach((_, i) => {
          const lengths = rows
            .map((row) => (row.cells[i]?.textContent || "").trim().length)
            .sort((a, b) => a - b);
          const median = lengths.length ? lengths[Math.floor(lengths.length / 2)] : 0;
          const size =
            median >= COL_LONG_CHARS ? "long" : median >= COL_MEDIUM_CHARS ? "medium" : null;
          // Full-width in card mode kicks in well before a desktop min-width does.
          const span = median >= CARD_SPAN_CHARS;
          if (!size && !span) return;
          const th = table.querySelectorAll<HTMLTableCellElement>("thead th")[i];
          if (th && size) th.dataset.col = size;
          rows.forEach((row) => {
            const cell = row.cells[i];
            if (!cell) return;
            if (size) cell.dataset.col = size;
            if (span) cell.dataset.span = "full";
          });
        });
      }

      // Collapse paragraph-length cells behind a toggle.
      const clamps: Array<{ cell: HTMLTableCellElement; inner: HTMLElement }> = [];
      rows.forEach((row) => {
        [...row.cells].forEach((cell) => {
          const text = (cell.textContent || "").trim();
          const len = text.length;
          if (NUMERIC_VALUE.test(text) && !BARE_YEAR.test(text)) cell.dataset.num = "true";
          // A column can be short on median and still hold the odd long value —
          // "Category" runs 19 characters across this table and 76 on one row.
          // That cell takes the card's full width (a four-line value crammed
          // into a ~200px field is what the median missed) but keeps the
          // value-led treatment of its column, so the same field doesn't read
          // as a value on one card and as commentary on the next.
          if (len >= CARD_SPAN_CHARS) cell.dataset.long = "true";
          if (len < LONG_CELL_CHARS) return;

          // Idempotent: React re-runs effects in StrictMode, and re-wrapping
          // would nest one clamp inside another.
          const existing = cell.querySelector<HTMLElement>(":scope > .cell-clamp");
          if (existing) {
            clamps.push({ cell, inner: existing });
            return;
          }

          const inner = document.createElement("div");
          inner.className = "cell-clamp";
          while (cell.firstChild) inner.appendChild(cell.firstChild);
          cell.appendChild(inner);

          const toggle = document.createElement("button");
          toggle.type = "button";
          toggle.className = "cell-more";
          toggle.setAttribute("aria-expanded", "false");
          // Chevron turns on expand; the label is its own span so toggling the
          // text can't wipe out the icon.
          toggle.innerHTML =
            '<span class="cell-more-text">Show more</span>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" ' +
            'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
          const toggleText = toggle.querySelector<HTMLElement>(".cell-more-text")!;
          // In card mode every field carries the same "Show more" — name the
          // field so the control is distinguishable out of context.
          const field = cell.dataset.label;
          const relabel = (expanded: boolean) => {
            const verb = expanded ? "Show less" : "Show more";
            toggleText.textContent = verb;
            toggle.setAttribute("aria-label", field ? `${verb} — ${field}` : verb);
          };
          relabel(false);
          toggle.addEventListener("click", () => {
            const expanded = cell.dataset.expanded === "true";
            cell.dataset.expanded = String(!expanded);
            toggle.setAttribute("aria-expanded", String(!expanded));
            relabel(!expanded);
          });
          cell.appendChild(toggle);

          cell.dataset.expanded = "false";
          // An outlier long cell in an otherwise-short column: the column-level
          // median missed it, but a clamp + "Show more" pill in a ~200px field
          // is unreadable, so span it too.
          cell.dataset.span = "full";
          clamps.push({ cell, inner });
        });
      });

      let max = 0;
      let raf = 0;

      const syncPos = () => {
        wrapEl.dataset.atStart = String(table.scrollLeft <= 1);
        wrapEl.dataset.atEnd = String(table.scrollLeft >= max - 1);
      };

      // Show a toggle only where the text is actually cut off. Height is
      // meaningful while collapsed; an expanded cell keeps its toggle.
      const syncClamps = () => {
        clamps.forEach(({ cell, inner }) => {
          if (cell.dataset.expanded === "true") return;
          cell.dataset.clamped = String(inner.scrollHeight - inner.clientHeight > 4);
        });
      };

      const asCards = () => wrapEl.classList.contains("as-cards");

      const measure = () => {
        // Card mode has no horizontal overflow — measuring here would clear the
        // sticky `data-wide` flag that put it in card mode to begin with.
        if (asCards()) {
          wrapEl.dataset.scrollable = "false";
          table.removeAttribute("tabindex");
          syncClamps();
          return;
        }
        max = table.scrollWidth - table.clientWidth;
        const scrollable = max > 2;
        wrapEl.dataset.scrollable = String(scrollable);
        if (scrollable) wrapEl.dataset.wide = "true";
        // Keyboard access: only make it a focus stop while it actually scrolls.
        if (scrollable) table.setAttribute("tabindex", "0");
        else table.removeAttribute("tabindex");
        syncPos();
        syncClamps();
      };

      // Any properly headed table can be cards, so any of them offers the
      // choice — a reader who prefers cards shouldn't get them on some tables
      // and not others.
      const eligible = () => labelled;
      const wide = () => wrapEl.dataset.wide === "true";
      // Per table, and null until this table's own switch is used: the control
      // governs the table it sits above and nothing else. Until then the table
      // picks its own default — cards if it would need horizontal scrolling,
      // the table itself if it already fits.
      let view: "cards" | "table" | null = null;
      const effective = () => view ?? (wide() ? "cards" : "table");

      const syncMode = () => {
        const cards =
          eligible() &&
          (effective() === "cards" || (window.innerWidth < CARD_BREAKPOINT && wide()));
        wrapEl.classList.toggle("as-cards", cards);
        // Collapse long cells wherever a cell is a full-width block rather than
        // a column: every phone, and the card view at any width. Not in the
        // desktop table view, where the reader can see the whole column.
        wrapEl.dataset.collapse = String(cards || window.innerWidth < CARD_BREAKPOINT);
      };

      // Every table that can be cards gets the switch. Tables without a
      // `<thead>`, or whose rows don't match the header's cell count, can't be
      // labelled as fields, so they can't be cards and get no switch.
      let toggleEl: HTMLDivElement | null = null;
      const refreshToggle = () => {
        const active = effective();
        toggleEl
          ?.querySelectorAll<HTMLButtonElement>("button")
          .forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.view === active)));
      };
      const ensureToggle = () => {
        if (toggleEl || !eligible()) return;
        toggleEl = document.createElement("div");
        toggleEl.className = "table-view-toggle";
        toggleEl.setAttribute("role", "group");
        toggleEl.setAttribute("aria-label", "Table layout");
        (["cards", "table"] as const).forEach((v) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.dataset.view = v;
          btn.textContent = v === "cards" ? "Cards" : "Table";
          btn.addEventListener("click", () => {
            view = v;
            sync();
            refreshToggle();
          });
          toggleEl!.appendChild(btn);
        });
        // Sibling, not child: the wrapper is the fades' positioning context, so
        // anything inside it would sit under them.
        wrapEl.parentNode?.insertBefore(toggleEl, wrapEl);
        refreshToggle();
      };

      // Coalesce scroll bursts to one update per frame.
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          syncPos();
        });
      };

      const sync = () => {
        measure();
        syncMode();
        measure();
        ensureToggle();
      };

      sync();
      table.addEventListener("scroll", onScroll, { passive: true });
      const ro = new ResizeObserver(measure);
      ro.observe(table);

      syncs.push(sync);
      cleanups.push(() => {
        toggleEl?.remove();
        table.removeEventListener("scroll", onScroll);
        ro.disconnect();
        if (raf) cancelAnimationFrame(raf);
      });
    });

    // Re-run after layout can still shift: a web-font swap changes a table's
    // scrollWidth without resizing its box (so the ResizeObserver won't fire),
    // late images settle at `load`, and a rotation crosses the card breakpoint.
    const syncAll = () => syncs.forEach((s) => s());
    let cancelled = false;
    let resizeRaf = 0;
    const onResize = () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0;
        syncAll();
      });
    };
    document.fonts?.ready.then(() => {
      if (!cancelled) syncAll();
    });
    window.addEventListener("load", syncAll);
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("load", syncAll);
      window.removeEventListener("resize", onResize);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return null;
}
